"use strict";
// Signing in without leaving the storefront.
//
// The panel this renders into is the authentication shell the Hub already
// switches to when there is no session — same 500px, same scrim, same close.
// Only the content changes as someone moves between screens, which is the
// whole point: the storefront underneath is never navigated, so nothing
// about the page they were on is lost.
//
// Google and Discord are still anchors. Their consent screens are theirs,
// and a provider's authorization request has to be a top-level navigation —
// that is the protocol, not a limitation of this file. Email is the method
// that can happen here, and it does: three fetches, cookies set on the
// response, and the Hub re-renders in the same overlay.
//
// Every dimension is lifted from the identity origin's own auth screens so
// the two read as one surface: 66px rows 11px apart, the 40px chip, the
// 50px field, the 48px submit, the 48x52 code boxes 8px apart.
(function () {
    var CODE_LENGTH = 6;
    var TRAILING_SLASHES = /\/+$/;
    function t(hubEl, key, fallback) {
        var value = hubEl.dataset[key];
        return value && value.length > 0 ? value : fallback;
    }
    function esc(value) {
        return window.FlixHubFormat.escapeHtml(value);
    }
    var LABEL_STYLE = "display:block;font-size:13px;color:var(--dim);margin-bottom:6px";
    var FIELD_STYLE = "width:100%;height:50px;padding-inline:16px;border-radius:var(--radius-button);background:var(--bg2);" +
        "border:1px solid var(--border);color:var(--text);font-family:var(--body);font-size:15px;outline:none";
    var SUBMIT_STYLE = "display:inline-flex;align-items:center;justify-content:center;gap:8px;width:100%;min-height:48px;" +
        "margin-top:16px;padding:0 22px;border:none;border-radius:var(--radius-button);" +
        "background:linear-gradient(135deg,var(--accent),var(--accent2));color:#160d05;" +
        "font-family:var(--display);font-weight:800;font-size:15px;letter-spacing:.2px;cursor:pointer;" +
        "box-shadow:0 10px 30px -10px var(--glow),inset 0 1px 0 rgba(255,255,255,.4);transition:transform .22s";
    var CODE_BOX_STYLE = "width:48px;height:52px;text-align:center;border-radius:var(--radius-sm);background:var(--bg2);" +
        "border:1px solid var(--border);color:var(--text);font-family:var(--display);font-weight:800;" +
        "font-size:22px;outline:none";
    var QUIET_STYLE = "background:none;border:none;font-family:var(--body);font-size:14px;cursor:pointer;padding:6px";
    var KICKER_STYLE = "font-family:var(--mono);letter-spacing:3px;font-size:12px;color:var(--accent);margin:0;text-transform:uppercase";
    var HEADING_STYLE = "font-family:var(--display);font-weight:800;font-size:26px;line-height:1.16;margin:12px 0 0;color:var(--text)";
    var DIVIDER_STYLE = "display:flex;align-items:center;gap:12px;margin-top:26px;color:var(--faint);font-size:12px;" +
        "font-family:var(--body)";
    var DIVIDER_LINE_STYLE = "flex:1;height:1px;background:var(--border)";
    var ICON_ROW_STYLE = "display:flex;gap:12px;margin-top:18px;justify-content:center";
    var ICON_BUTTON_STYLE = "display:flex;align-items:center;justify-content:center;flex:none;width:52px;height:52px;" +
        "border-radius:50%;border:1px solid var(--border);text-decoration:none;cursor:pointer;" +
        "box-shadow:0 4px 14px -6px rgba(0,0,0,.5);transition:border-color .22s ease,transform .22s ease";
    var GOOGLE_MARK = '<svg viewBox="0 0 48 48" width="21" height="21" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41.4 36 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z"/></svg>';
    var DISCORD_MARK = '<svg viewBox="0 0 24 24" width="21" height="21" aria-hidden="true"><path fill="#fff" d="M20.32 4.57A19.8 19.8 0 0 0 15.42 3c-.24.43-.5 1-.68 1.46a18.3 18.3 0 0 0-5.49 0A12 12 0 0 0 8.56 3a19.7 19.7 0 0 0-4.9 1.57C.55 9.2-.3 13.7.13 18.15a19.9 19.9 0 0 0 6.03 3.05c.49-.66.92-1.36 1.29-2.1-.71-.27-1.39-.6-2.03-.99.17-.12.34-.25.5-.38a14.2 14.2 0 0 0 12.15 0c.17.14.34.26.5.38-.64.39-1.32.72-2.03.99.37.74.8 1.44 1.29 2.1a19.8 19.8 0 0 0 6.03-3.05c.5-5.16-.85-9.62-3.56-13.58zM8.02 15.42c-1.18 0-2.15-1.08-2.15-2.4 0-1.33.95-2.41 2.15-2.41 1.2 0 2.17 1.09 2.15 2.4 0 1.33-.95 2.41-2.15 2.41zm7.96 0c-1.18 0-2.15-1.08-2.15-2.4 0-1.33.95-2.41 2.15-2.41 1.2 0 2.17 1.09 2.15 2.4 0 1.33-.94 2.41-2.15 2.41z"/></svg>';
    var ALERT = '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex:none"><circle cx="12" cy="12" r="9"/><path d="M12 7v6"/><path d="M12 16h.01"/></svg>';
    /**
     * Google and Discord as a compact icon button rather than a full
     * description row — the row's chevron and subtitle existed to explain a
     * choice between three equal methods; email is the primary field on this
     * screen now; Google/Discord are the fast path next to it, not a list.
     */
    function providerIconButtonHtml(input) {
        return ('<a href="' +
            esc(input.href) +
            '" aria-label="' +
            esc(input.label) +
            '" title="' +
            esc(input.label) +
            '" style="' +
            ICON_BUTTON_STYLE +
            ";background:" +
            input.background +
            '" style-hover="border-color:var(--borderHi);transform:translateY(-2px)">' +
            input.mark +
            "</a>");
    }
    function errorHtml(message) {
        return ('<p data-flix-signin-error role="alert" style="display:flex;align-items:center;justify-content:center;' +
            'gap:6px;margin:14px 0 0;color:var(--error);font-size:13px">' +
            ALERT +
            esc(message) +
            "</p>");
    }
    /**
     * Where a method that must leave the storefront goes.
     *
     * Always /auth/start, never /auth/{provider}/start directly. That second
     * URL finishes an OIDC interaction, and only /auth/start creates one, so
     * linking at it is how someone gets authenticated by Google and then
     * handed a 400 -- a failure this project has already seen once in
     * staging. /auth/start creates the interaction, notes the method, and the
     * identity origin forwards without showing its own copy of this screen.
     *
     * return_to is the page the overlay was opened on, so the round trip ends
     * where it began rather than on a fixed account page. The identity origin
     * checks it against its own allowlist; an origin not on it is ignored.
     */
    function providerHref(method) {
        var _a, _b;
        var base = ((_b = (_a = window.flixIdentityConfig) === null || _a === void 0 ? void 0 : _a.apiBaseUrl) !== null && _b !== void 0 ? _b : "").replace(TRAILING_SLASHES, "");
        var url = new URL(base + "/auth/start");
        var locale = document.documentElement.lang || "";
        if (locale)
            url.searchParams.set("ui_locales", locale);
        url.searchParams.set("method", method);
        url.searchParams.set("return_to", returnTo());
        return url.toString();
    }
    /** The storefront page as it is now, minus any marker a previous return left. */
    function returnTo() {
        var here = new URL(window.location.href);
        here.searchParams.delete("flix_account");
        here.searchParams.delete("auth_error");
        return here.toString();
    }
    /**
     * The invitation, the email field and the fast-path providers, all in one
     * screen — this used to be three: an empty state with a "Sign in" button,
     * a list of three equally-weighted methods, and a separate email field
     * behind that. Email is the one method that never leaves this panel
     * (Google and Discord are a top-level navigation by protocol), so it is
     * the field on the page rather than a fourth thing to click through to.
     *
     * This is also the root of the flow: closing the overlay is the only way
     * out of it, which is why it renders no "Back".
     */
    function renderWelcome(hubEl, contentEl, email, error) {
        contentEl.innerHTML =
            '<div data-flix-signin="welcome">' +
                '<p style="' +
                KICKER_STYLE +
                '">' +
                esc(t(hubEl, "i18nSignInKicker", "Sign in")) +
                "</p>" +
                '<h2 style="' +
                HEADING_STYLE +
                '">' +
                esc(t(hubEl, "i18nSignInHeading", "Continue to your Flix Account")) +
                "</h2>" +
                '<form data-flix-signin-email-form style="display:flex;flex-direction:column;margin-top:26px">' +
                '<label for="flix-signin-email" style="' +
                LABEL_STYLE +
                '">' +
                esc(t(hubEl, "i18nEmailLabel", "Email")) +
                "</label>" +
                '<input id="flix-signin-email" name="email" type="email" inputmode="email" autocomplete="email" ' +
                'autocapitalize="off" spellcheck="false" required value="' +
                esc(email) +
                '" style="' +
                FIELD_STYLE +
                '" style-focus="border-color:var(--accent);box-shadow:0 0 0 4px var(--glow)">' +
                (error ? errorHtml(error) : "") +
                '<button type="submit" data-flix-signin-send style="' +
                SUBMIT_STYLE +
                '" style-hover="transform:translateY(-2px)">' +
                esc(t(hubEl, "i18nEmailSend", "Send code")) +
                "</button>" +
                "</form>" +
                '<div style="' +
                DIVIDER_STYLE +
                '"><span style="' +
                DIVIDER_LINE_STYLE +
                '"></span><span>' +
                esc(t(hubEl, "i18nSignInDivider", "Or continue with")) +
                '</span><span style="' +
                DIVIDER_LINE_STYLE +
                '"></span></div>' +
                '<div style="' +
                ICON_ROW_STYLE +
                '">' +
                providerIconButtonHtml({
                    href: providerHref("google"),
                    background: "#ffffff",
                    mark: GOOGLE_MARK,
                    label: t(hubEl, "i18nMethodGoogle", "Continue with Google"),
                }) +
                providerIconButtonHtml({
                    href: providerHref("discord"),
                    background: "#5865f2",
                    mark: DISCORD_MARK,
                    label: t(hubEl, "i18nMethodDiscord", "Continue with Discord"),
                }) +
                "</div>" +
                "</div>";
        window.flixCompileHoverStyles(contentEl);
        var input = contentEl.querySelector("#flix-signin-email");
        if (input)
            input.focus();
    }
    function renderCode(hubEl, contentEl, challengeId, email, error) {
        var boxes = "";
        for (var index = 0; index < CODE_LENGTH; index += 1) {
            boxes +=
                '<input class="flix-signin-digit" type="text" inputmode="numeric" maxlength="1" ' +
                    'pattern="[0-9]" aria-label="' +
                    esc(t(hubEl, "i18nCodeLabel", "Code")) +
                    " " +
                    (index + 1) +
                    '" autocomplete="' +
                    (index === 0 ? "one-time-code" : "off") +
                    '" style="' +
                    CODE_BOX_STYLE +
                    (error ? ";border-color:var(--error)" : "") +
                    '" style-focus="border-color:var(--accent);box-shadow:0 0 0 4px var(--glow)">';
        }
        contentEl.innerHTML =
            '<div data-flix-signin="code" data-flix-signin-challenge="' +
                esc(challengeId) +
                '" data-flix-signin-email="' +
                esc(email) +
                '" style="text-align:center">' +
                '<p style="' +
                KICKER_STYLE +
                '">' +
                esc(t(hubEl, "i18nEmailLabel", "Email")) +
                "</p>" +
                '<h2 style="' +
                HEADING_STYLE +
                '">' +
                esc(t(hubEl, "i18nCodeHeading", "Enter the code")) +
                "</h2>" +
                '<p style="color:var(--dim);font-size:14px;margin:12px 0 0;word-break:break-all">' +
                esc(t(hubEl, "i18nCodeSentTo", "We sent a six-digit code to")) +
                " " +
                esc(email) +
                "</p>" +
                '<form data-flix-signin-code-form style="display:flex;flex-direction:column;margin-top:0">' +
                '<div dir="ltr" style="display:flex;gap:8px;justify-content:center;margin-top:24px">' +
                boxes +
                "</div>" +
                (error ? errorHtml(error) : "") +
                '<button type="submit" data-flix-signin-confirm style="' +
                SUBMIT_STYLE +
                '" style-hover="transform:translateY(-2px)">' +
                esc(t(hubEl, "i18nCodeConfirm", "Confirm")) +
                "</button>" +
                "</form>" +
                '<div style="margin-top:18px;display:flex;flex-direction:column;gap:8px;align-items:center">' +
                '<button type="button" data-flix-signin-action="resend" style="' +
                QUIET_STYLE +
                ';color:var(--accent)">' +
                esc(t(hubEl, "i18nCodeResend", "Send a new code")) +
                "</button>" +
                '<button type="button" data-flix-signin-action="welcome" style="' +
                QUIET_STYLE +
                ';color:var(--dim)" style-hover="color:var(--text)">' +
                esc(t(hubEl, "i18nBack", "Back")) +
                "</button>" +
                "</div>" +
                "</div>";
        window.flixCompileHoverStyles(contentEl);
        var first = contentEl.querySelector(".flix-signin-digit");
        if (first)
            first.focus();
    }
    /**
     * One sentence per failure the reader can act on, and one generic line for
     * everything else — so an error added on the server later cannot start
     * surfacing its internals here.
     */
    function messageFor(hubEl, err) {
        var code = typeof err === "object" && err !== null && "code" in err
            ? String(err.code)
            : "";
        if (code === "IDENTITY.INVALID_EMAIL_ADDRESS") {
            return t(hubEl, "i18nEmailInvalid", "Check the email format.");
        }
        if (code === "AUTHENTICATION.TOO_MANY_OTP_REQUESTS") {
            return t(hubEl, "i18nEmailTooMany", "Too many attempts. Wait a moment and try again.");
        }
        if (code === "OTP.CHALLENGE_EXPIRED") {
            return t(hubEl, "i18nCodeExpired", "That code expired. Ask for a new one.");
        }
        if (code === "OTP.CHALLENGE_ALREADY_CONSUMED") {
            return t(hubEl, "i18nCodeUsed", "That code has already been used. Ask for a new one.");
        }
        if (code.indexOf("OTP.") === 0) {
            return t(hubEl, "i18nCodeWrong", "That code is not right. Check it and try again.");
        }
        return t(hubEl, "i18nEmailFailed", "That did not work. Try again.");
    }
    function busy(button, isBusy) {
        if (!button)
            return;
        button.disabled = isBusy;
        button.style.opacity = isBusy ? "0.6" : "";
    }
    window.FlixHubSignIn = {
        renderWelcome: renderWelcome,
        renderCode: renderCode,
        messageFor: messageFor,
        busy: busy,
        CODE_LENGTH: CODE_LENGTH,
    };
})();
